import React, { FunctionComponent, InputHTMLAttributes, useCallback, useEffect, useRef } from 'react';
import { UseFormMethods } from 'react-hook-form';
import useTranslate from 'Hooks/useTranslate';
import { EMAIL_PATTERN } from 'SharedHelpers/validatation';
import Input from 'SharedComponents/Input';

export const EMAIL_PLACEHOLDER_ANIM_START_DELAY = 6;

type ErrorCallback = (this: HTMLInputElement, ev: Event) => void;

interface Props extends InputHTMLAttributes<HTMLInputElement> {
    backendEmailValidator: Function;
    formApi: UseFormMethods;
    autoFocus?: boolean;
    errorCallback?: ErrorCallback;
}

const useAutoFocus = (autoFocus: boolean, ref: React.RefObject<HTMLInputElement>) => {
    useEffect(() => {
        if (autoFocus && ref.current) {
            ref.current.focus();
        }
    }, [autoFocus, ref]);
};

const useErrorCallback = (errorCallback: ErrorCallback, ref: React.RefObject<HTMLInputElement>) => {
    const fieldCallback = useCallback(errorCallback, [errorCallback]);

    useEffect(() => {
        const current = ref?.current;
        if (current?.addEventListener) {
            current.addEventListener('invalid', fieldCallback);
        }
        return () => {
            if (current?.removeEventListener) {
                current.removeEventListener('invalid', fieldCallback);
            }
        };
    }, [ref, fieldCallback]);
};

const EmailInput: FunctionComponent<Props> = props => {
    const { backendEmailValidator, formApi, ...rest } = props;
    const ref = useRef<HTMLInputElement | null>(null);
    const { autoFocus = false, errorCallback = () => ({}) } = props;
    const { tPlain } = useTranslate();

    useAutoFocus(autoFocus, ref);
    useErrorCallback(errorCallback, ref);

    return (
        <Input
            type="email"
            name="email"
            ref={inputEl => {
                formApi.register(inputEl, {
                    required: tPlain('signupOverlay_Please enter a valid email address'),
                    pattern: {
                        value: EMAIL_PATTERN,
                        message: tPlain('signupOverlay_Please enter a valid email address'),
                    },
                    validate: () => backendEmailValidator(formApi.getValues()),
                });

                ref.current = inputEl;
            }}
            className="qa-login-signup-form__email"
            showError={!!formApi.errors.email}
            placeholder={tPlain('signupOverlay_Email placeholder')}
            errorMsg={formApi.errors?.email?.message}
            errorType={formApi.errors?.email?.type}
            {...rest}
        />
    );
};

export default EmailInput;
